import HeroPlayButton from "./HeroPlayButton";
import HeroPlaylistThumb from "./HeroPlaylistThumb";
import HeroProviderLogo from "./HeroProviderLogo";
import HeroRandomGameButton from "./HeroRandomGameButton";
import HeroThumbStack from "./HeroThumbStack";
import type { HighlightedGameSlide } from "./types";

type Props = {
  slide: HighlightedGameSlide;
  providerName: string;
  logoUrl?: string;
  totalGamesCount?: number;
  randomGameHrefs?: string[];
  thumbnailUrls: string[];
  translations: Record<string, string>;
};

const playButtonClassName = "h-8 gap-1 px-2.5 text-xs [&_svg]:size-4";

const HeroCopyCompact: FC<Props> = ({
  slide,
  providerName,
  logoUrl,
  totalGamesCount,
  randomGameHrefs,
  thumbnailUrls,
  translations,
}) => {
  const playLabel =
    translations["widget.highlighted-games.play"] ??
    translations["widget.highlighted-games.play-now"];
  const stats = [
    totalGamesCount !== undefined &&
      `${totalGamesCount}+ ${translations["widget.highlighted-games.games"]}`,
    !!slide.likes &&
      `${slide.likes} ${translations["widget.highlighted-games.likes"]}`,
  ].filter(Boolean);

  return (
    <div
      data-slot="hero-copy-compact"
      className="flex flex-col items-center gap-4 px-3 pt-1 pb-3 md:hidden"
    >
      {(logoUrl || slide.description) && (
        <div className="flex w-full flex-col items-center gap-1 py-1">
          {logoUrl && (
            <HeroProviderLogo
              url={logoUrl}
              providerName={providerName}
              className="ml-0 h-12 max-w-27"
            />
          )}
          {slide.description && (
            <p className="line-clamp-2 w-full px-3 text-center text-white/80 text-xs/4">
              {slide.description}
            </p>
          )}
        </div>
      )}
      <div className="flex w-full items-center justify-between gap-3 rounded-xl bg-card p-2.5">
        <div className="flex min-w-0 items-center gap-3">
          {slide.isProvider ? (
            <HeroThumbStack thumbnailUrls={thumbnailUrls} />
          ) : (
            <span className="flex h-10 shrink-0">
              <HeroPlaylistThumb
                thumbnailUrl={slide.thumbnailUrl}
                alt=""
                isSelected={false}
              />
            </span>
          )}
          <div className="flex min-w-0 flex-col">
            <h2 className="truncate font-headline! font-semibold text-foreground text-sm/5">
              {slide.title}
            </h2>
            {stats.length > 0 && (
              <p className="truncate font-medium text-muted-foreground text-xs/4">
                {stats.join(" · ")}
              </p>
            )}
          </div>
        </div>
        {slide.isProvider
          ? randomGameHrefs && (
              <HeroRandomGameButton
                hrefs={randomGameHrefs}
                label={playLabel}
                className={playButtonClassName}
              />
            )
          : slide.href && (
              <HeroPlayButton
                href={slide.href}
                label={playLabel}
                className={playButtonClassName}
              />
            )}
      </div>
    </div>
  );
};

export default HeroCopyCompact;
